import type {CSSObject} from "@emotion/react";
import styled from "@emotion/styled";

export const ContentWrapper = styled.div<{styles?: CSSObject}>(({theme, styles}) => ({
  gridColumn: theme?.breakpoints?.isMobile
    ? "1/-1"
    : theme?.breakpoints?.md
      ? "3/15"
      : "4/14",
  ...styles,
}));

/**
 * Multi-Column Container with advanced break control
 *
 * Break Strategy:
 * - Allows breaks BETWEEN sentences (natural paragraph flow)
 * - Prevents breaks INSIDE sentences (orphans/widows control)
 * - Prevents orphaned punctuation (text-wrap: pretty)
 * - Keeps headings with following content
 *
 * Supersedes a JS sentence-splitting approach, dropped in June 2026: it broke
 * WYSIWYG markup and left columns unbalanced.
 */
export const MultiColumnContainer = styled.div<{isMobile?: boolean; md?: boolean}>(
  ({isMobile, md}) => ({
    gridColumn: isMobile ? "1/-1" : md ? "3/15" : "4/14",
    columnCount: isMobile ? 1 : 2,
    columnGap: isMobile ? 0 : "20px",
    columnFill: "balance",

    // Orphan/widow control - prevent single lines at column breaks
    orphans: 3, // Minimum 3 lines at bottom of column
    widows: 3, // Minimum 3 lines at top of next column

    // Hyphenation control for better text flow
    hyphens: "auto",
    WebkitHyphens: "auto",
    MozHyphens: "auto",
    msHyphens: "auto",
    hyphenateLimitChars: "8 4 4", // min word length, min chars before/after hyphen

    // Advanced text breaking rules to prevent orphaned punctuation
    // This CSS prevents line breaks immediately after weak punctuation
    "& p, & li": {
      // Keep punctuation with following word
      textWrap: "pretty", // Modern CSS for better text wrapping
      WebkitTextWrap: "pretty",

      // Prevent breaks after common French/European punctuation patterns
      // Using CSS to add non-breaking space behavior after : ; ,
      "& ::after": {
        content: '""',
        whiteSpace: "nowrap",
      },
    },
  }),
);
